/* ru-vocab-app.js — UI for the adaptive Russian vocabulary test.
 * Thin DOM layer over ru-vocab-engine.js (which carries the psychometrics).
 * Telemetry mirrors iq-test: tool_use → tool_stage(from→to) → tool_complete. */
'use strict';

const RVT = window.__RVT || { T: {}, cfg: {} };
const T = RVT.T || {};
const cfg = RVT.cfg || {};
const t = (k, d) => (T[k] != null ? T[k] : (d != null ? d : k));
const ae = (type, payload) => { try { window.__ae && window.__ae(type, Object.assign({ tool_slug: 'russian-vocabulary-test' }, payload || {})); } catch (e) {} };

const root = document.getElementById('rvtApp');
const panel = () => document.getElementById('rvtPanel');
const SLUG = 'russian-vocabulary-test';
// The start screen is SERVER-rendered (#rvtStartScreen in the blade) so it is
// on screen at first paint: this module script is deferred and wakes only after
// the whole layout has parsed, then waits for the engine and the word banks. The
// node is kept and re-shown by renderStart(); a tap that landed on it before
// boot() is handed over by window.__earlyAction (layout head) — see claimEarly().
const startNode = document.getElementById('rvtStartScreen');
const esc = s => String(s).replace(/[&<>"]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));

/* Фаза страницы: карточка документа видна только на экране результата. Перед
 * любой перерисовкой панели карточка возвращается «домой» (вне панели) —
 * innerHTML уничтожил бы её вместе с серверными текстами и обработчиками. */
function setPhase(phase) {
    const wrap = root && root.closest('.rvt-wrap');
    if (phase !== 'results') { try { window.RVTReport && window.RVTReport.unmount(); } catch (e) {} }
    if (wrap) { if (phase) wrap.dataset.phase = phase; else delete wrap.dataset.phase; }
}

let Eng = null, bank = null, sizeModel = null, mcqItems = [], litItems = [], sess = null, current = null, askedAt = 0;

async function boot() {
    try {
        const v = cfg.v || '1';
        Eng = await import('./ru-vocab-engine.js?v=' + encodeURIComponent(v));
        const base = cfg.base || '/data/lexicon/ru-vocab';
        const vq = '?v=' + encodeURIComponent(cfg.v || '1');   // data banks change with the build → cache-bust with the graph version
        const j = (f, fb) => fetch(base + '/' + f + vq).then(r => r.ok ? r.json() : fb).catch(() => fb);
        let mcqData, litData;
        [bank, sizeModel, mcqData, litData] = await Promise.all([
            fetch(base + '/yesno.json' + vq).then(r => { if (!r.ok) throw new Error('yesno'); return r.json(); }),
            fetch(base + '/size-model.json' + vq).then(r => { if (!r.ok) throw new Error('size'); return r.json(); }),
            j('mcq.json', { items: [] }),
            j('literacy.json', { items: [] }),
        ]);
        mcqItems = (mcqData && mcqData.items) || [];
        litItems = (litData && litData.items) || [];
    } catch (e) {
        if (panel()) panel().innerHTML = `<div class="rvt-card rvt-center">${esc(t('load_error', 'Не удалось загрузить тест. Обновите страницу.'))}</div>`;
        claimEarly(false);
        return;
    }
    renderStart();
    claimEarly(!!startNode);
}

/** Take the start buttons over from window.__earlyAction — only once the engine
    and the banks are loaded (start() and the trainer need both); on a failed
    load the tap is dropped, otherwise its button would spin forever. */
function claimEarly(replay) {
    if (!window.__earlyAction) return;
    window.__earlyAction.claim(SLUG, replay ? (a) => { if (a === 'practice') renderHub(); else start(); } : null);
}

// The blade owns this screen's markup (one source of truth). A page served from
// a cache older than the blade has no #rvtStartScreen — say so honestly instead
// of keeping a second copy of the markup here.
let startWired = false;
function renderStart() {
    pr = null;
    setPhase(null);
    const p = panel();
    if (!startNode) { p.innerHTML = `<div class="rvt-card rvt-center">${esc(t('load_error', 'Не удалось загрузить тест. Обновите страницу.'))}</div>`; return; }
    if (!startWired) {
        startWired = true;
        document.getElementById('rvtStart').addEventListener('click', start);
        document.getElementById('rvtPractice').addEventListener('click', renderHub);
    }
    if (p.firstElementChild !== startNode || p.children.length !== 1) {
        p.textContent = '';
        p.appendChild(startNode);
    }
}

function start() {
    pr = null;
    const testLit = litItems.filter(m => TEST_CONSTRUCTS.indexOf(m.construct) > -1);
    sess = Eng.createVocabSession(bank, sizeModel, { seed: (Date.now() & 0x7fffffff) || 1, nowMs: Date.now(), mcq: mcqItems, literacy: testLit });
    ae('tool_use', {});
    ae('tool_stage', { from: null, to: 'start' });
    step();
}

function step() {
    const s = sess.next();
    if (s.done) return finish();
    current = s.unit;
    askedAt = Date.now();
    const prog = Math.round(sess.progress() * 100);
    // funnel: emit when a new section (verify/literacy) starts → drop-off visible
    if (s.sectionChanged && s.section) ae('tool_stage', { from: null, to: s.section, served: sess.served && sess.served().real });
    if (current.kind === 'mcq') return renderMcq(current, prog, s.sectionChanged);
    if (current.kind === 'lit') return renderLit(current, prog, s.sectionChanged);
    panel().innerHTML = `
      <div class="rvt-card">
        <div class="rvt-prog"><div class="rvt-prog-f" style="width:${prog}%"></div></div>
        <p class="rvt-instr">${esc(t('yesno_instruction', 'Знаете значение этого слова?'))}</p>
        <div class="rvt-word">${esc(current.word)}</div>
        <div class="rvt-yn">
          <button class="rvt-btn ghost rvt-no" id="rvtNo"><span class="rvt-kbd">←</span> ${esc(t('btn_dont_know', 'Не знаю'))}</button>
          <button class="rvt-btn rvt-yes" id="rvtYes">${esc(t('btn_know', 'Знаю'))} <span class="rvt-kbd">→</span></button>
        </div>
      </div>`;
    document.getElementById('rvtYes').addEventListener('click', () => answer(true));
    document.getElementById('rvtNo').addEventListener('click', () => answer(false));
}

/* Shared question view — used by both the test and the practice trainer so the
   markup stays identical. Returns {stem, sentence, optCls} for any mcq/lit unit. */
function unitView(u) {
    if (u.kind === 'mcq') {
        const stem = u.mtype === 'syn'
            ? t('mcq_syn_stem', 'Выберите синоним к слову «:w»').replace(':w', u.word)
            : t('mcq_def_stem', 'Что означает слово «:w»?').replace(':w', u.word);
        return { stem, sentence: '', optCls: '' };
    }
    const c = u.construct;
    if (c === 'stress') return { stem: t('lit_stress_stem', 'Выберите слово с верным ударением'), sentence: '', optCls: ' rvt-opt-stress' };
    if (c === 'morphform') return { stem: t('lit_morphform_stem', 'Найдите вариант с ошибкой в образовании формы слова'), sentence: '', optCls: '' };
    if (c === 'nn' || c === 'split' || c === 'ne' || c === 'prepri' || c === 'root')
        return { stem: t('lit_spelling_stem', 'Найдите слово, написанное с ошибкой'), sentence: '', optCls: '' };
    if (c === 'pleonasm') return { stem: t('lit_pleonasm_stem', 'Какое слово лишнее? (речевая избыточность)'), sentence: (u._item && u._item.sentence) || '', optCls: ' rvt-opt-stress' };
    if (c === 'punct' || c === 'punct2') return { stem: t('lit_punct_stem', 'Выберите верную расстановку запятых'), sentence: '', optCls: ' rvt-opt-sentence' };
    if (c === 'orfoep') {
        const sub = (u._item && u._item.subtype) || 'chn';
        return { stem: sub === 'harde' ? t('lit_orfoep_harde_stem', 'В каком слове согласный перед Е произносится ТВЁРДО?') : t('lit_orfoep_chn_stem', 'В каком слове ЧН произносится как [ШН]?'), sentence: '', optCls: ' rvt-opt-stress' };
    }
    return { stem: t('lit_paronym_stem', 'Что означает слово «:w»?').replace(':w', u.word), sentence: '', optCls: '' };
}
function optsHtml(options, optCls) {
    return options.map((o, i) =>
        `<button class="rvt-opt${optCls}" data-i="${i}"><span class="rvt-kbd">${i + 1}</span><span>${esc(o)}</span></button>`).join('');
}

function renderMcq(u, prog, first) {
    const v = unitView(u);
    const intro = first ? `<p class="rvt-section">${esc(t('verify_intro', 'Почти готово! Короткая проверка понимания — выберите верное значение.'))}</p>` : '';
    panel().innerHTML = `
      <div class="rvt-card">
        <div class="rvt-prog"><div class="rvt-prog-f" style="width:${prog}%"></div></div>
        ${intro}
        <p class="rvt-mcq-stem">${esc(v.stem)}</p>
        <div class="rvt-opts">${optsHtml(u.options, v.optCls)}</div>
      </div>`;
    panel().querySelectorAll('.rvt-opt').forEach(b => b.addEventListener('click', () => answerMcq(parseInt(b.dataset.i, 10))));
}

function answer(knows) {
    if (!current) return;
    sess.record(current, knows);
    current = null;
    step();
}
function renderLit(u, prog, first) {
    const v = unitView(u);
    const intro = first ? `<p class="rvt-section">${esc(t('lit_intro', 'И последнее — короткая проверка грамотности (в духе ЕГЭ).'))}</p>` : '';
    const sentHtml = v.sentence ? `<p class="rvt-lit-sentence">${esc(v.sentence)}</p>` : '';
    panel().innerHTML = `
      <div class="rvt-card">
        <div class="rvt-prog"><div class="rvt-prog-f" style="width:${prog}%"></div></div>
        ${intro}
        <p class="rvt-mcq-stem">${esc(v.stem)}</p>
        ${sentHtml}
        <div class="rvt-opts">${optsHtml(u.options, v.optCls)}</div>
      </div>`;
    panel().querySelectorAll('.rvt-opt').forEach(b => b.addEventListener('click', () => answerMcq(parseInt(b.dataset.i, 10))));
}

function answerMcq(i) {
    if (!current || (current.kind !== 'mcq' && current.kind !== 'lit')) return;
    sess.record(current, i);
    current = null;
    step();
}

function litResultHtml(r) {
    const pct = Math.round(r.literacyScore * 100);
    const names = {
        stress: t('construct_stress', 'Ударения'),
        paronym: t('construct_paronym', 'Паронимы'),
        pleonasm: t('construct_pleonasm', 'Лексические нормы'),
        morphform: t('construct_morphform', 'Формы слова'),
        punct: t('construct_punct', 'Пунктуация'),
    };
    const chips = Object.keys(r.literacyByConstruct || {}).map(k => {
        const c = r.literacyByConstruct[k];
        return `<button class="rvt-lit-chip rvt-lit-chip-btn" data-topic="${esc(k)}">${esc(names[k] || k)} ${c.correct}/${c.n} ›</button>`;
    }).join('');
    return `
      <div class="rvt-litbox">
        <div class="rvt-lit-h">${esc(t('result_literacy_title', 'Грамотность'))}</div>
        <div class="rvt-lit-score">${pct}%</div>
        <div class="rvt-lit-sub">${esc(t('result_literacy_sub', ':c из :n верно').replace(':c', r.litCorrect).replace(':n', r.litN))}</div>
        ${chips ? `<div class="rvt-lit-chips">${chips}</div>` : ''}
      </div>`;
}

function onKey(e) {
    const kd = (e.ctrlKey || e.metaKey || e.altKey) ? '' : (window.__keyLetter ? window.__keyLetter(e) : e.key);
    if (pr) {  // practice trainer keyboard
        if (pr.answered) { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); const n = document.getElementById('rvtNext'); if (n) n.click(); } return; }
        if (pr.current) { const n = parseInt(kd, 10); if (n >= 1 && n <= pr.current.options.length) { e.preventDefault(); const b = panel().querySelector(`.rvt-opt[data-i="${n - 1}"]`); if (b) practicePick(n - 1, b); } }
        return;
    }
    if (!current) return;
    if (current.kind === 'mcq' || current.kind === 'lit') {
        const n = parseInt(kd, 10);
        if (n >= 1 && n <= 4 && n <= current.options.length) { e.preventDefault(); answerMcq(n - 1); }
        return;
    }
    if (e.key === 'ArrowRight' || kd === '2') { e.preventDefault(); answer(true); }
    else if (e.key === 'ArrowLeft' || kd === '1') { e.preventDefault(); answer(false); }
}

function finish() {
    panel().innerHTML = `<div class="rvt-card rvt-center"><div class="rvt-spinner"></div><p>${esc(t('computing', 'Считаем результат…'))}</p></div>`;
    const r = sess.finalize({ nowMs: Date.now() });
    ae('tool_stage', { from: 'test', to: 'complete', vocab: r.vocabSize, level: r.level, percentile: r.percentile, false_alarm: r.falseAlarm, n_real: r.nReal, reliable: r.reliable, dur_ms: r.durMs });
    ae('tool_complete', { vocab: r.vocabSize, level: r.level, percentile: r.percentile });
    setTimeout(() => renderResult(r), 350);
}

function renderResult(r) {
    const levelName = t('level_' + r.level, r.level);
    const levelDesc = t('leveldesc_' + r.level, '');
    const unreliable = r.flags.includes('overclaim') || r.flags.includes('verify_low');
    const honesty = unreliable
        ? `<div class="rvt-warn">${esc(r.flags.includes('verify_low')
            ? t('verify_bad', 'Проверка показала, что часть отмеченных слов вы знаете не так уверенно — оценка завышена. Попробуйте пройти честнее.')
            : t('honesty_bad', 'Вы отметили слишком много выдуманных слов — оценка, скорее всего, завышена. Попробуйте ещё раз честно.'))}</div>`
        : (r.nPseudo ? `<p class="rvt-honesty">${esc(t('honesty_ok', 'Честность подтверждена: выдуманные слова вы почти не отмечали.'))}</p>` : '');
    const mcLine = r.mcN ? `<p class="rvt-honesty">${esc(t('verify_result', 'Проверка понимания: :c из :n верно').replace(':c', r.mcCorrect).replace(':n', r.mcN))}</p>` : '';
    const litBlock = r.litN ? litResultHtml(r) : '';
    const learnBlock = (r.learnWords && r.learnWords.length >= 2) ? `
        <div class="rvt-learn">
          <div class="rvt-learn-h">${esc(t('learn_title', 'Слова, которые стоит узнать'))}</div>
          ${r.learnWords.map(w => `<div class="rvt-learn-item"><b>${esc(w.word)}</b> — ${esc(w.gloss)}</div>`).join('')}
        </div>` : '';
    // Нет таблицы мест — нет строки: место, которого мы не мерили, не пишем.
    const pctlLine = r.percentile != null
        ? `<div class="rvt-pctl">${esc(t('result_percentile', 'Это больше, чем у :p прошедших тест').replace(':p', r.percentile + '%'))}</div>`
        : '';

    panel().innerHTML = `
      <div class="rvt-card rvt-center rvt-result">
        <div class="rvt-eyebrow">${esc(t('result_eyebrow', 'Ваш словарный запас'))}</div>
        <div class="rvt-number" id="rvtNum">0</div>
        <div class="rvt-number-unit">${esc(t('result_words', 'слов'))}</div>
        <div class="rvt-level">${esc(levelName)}</div>
        ${levelDesc ? `<div class="rvt-leveldesc">${esc(levelDesc)}</div>` : ''}
        ${pctlLine}
        ${honesty}
        ${mcLine}
        <div id="rvtOfferSlot"></div>
        <div class="rvt-stats">
          <div class="rvt-stat"><b>${r.nReal}</b><span>${esc(t('stat_words_shown', 'слов показано'))}</span></div>
          <div class="rvt-stat"><b>${Math.round(r.hitRate * 100)}%</b><span>${esc(t('stat_known', 'отмечено знакомыми'))}</span></div>
          <div class="rvt-stat"><b>${r.nPseudo}</b><span>${esc(t('stat_fakes', 'слов-ловушек'))}</span></div>
        </div>
        ${litBlock}
        ${learnBlock}
        ${reviewHtml(r)}
        <div class="rvt-actions">
          <button class="rvt-btn" id="rvtShare">${esc(t('btn_share', 'Поделиться'))}</button>
          <button class="rvt-btn ghost" id="rvtRetake">${esc(t('btn_retake', 'Пройти заново'))}</button>
        </div>
        ${litBlock ? `<button class="rvt-btn ghost rvt-practice-cta" id="rvtToPractice">${esc(t('practice_cta', 'Тренажёр грамотности по темам'))}</button>` : ''}
        <details class="rvt-method">
          <summary>${esc(t('method_summary', 'Как это посчитано?'))}</summary>
          <p>${esc(t('method_body', 'Тест показывает слова разной частотности и по вашим ответам строит статистическую оценку (модель IRT). Выдуманные слова измеряют склонность к угадыванию и корректируют итог. Оценка приблизительная (±10%) и охватывает наиболее употребительные слова русского языка.'))}</p>
          <p class="rvt-attr">${esc(t('method_attr', 'Данные: ru.Wiktionary (CC BY-SA), частотный словарь НКРЯ (Ляшевская, Шаров).'))}</p>
        </details>
      </div>`;

    animateNumber(document.getElementById('rvtNum'), r.vocabSize);
    setPhase('results');
    // Сертификат и разбор (PDF). Отсутствие модуля не должно ломать экран
    // результата — он ценен сам по себе и без документа.
    try { window.RVTReport && window.RVTReport.mount(r); } catch (e) {}
    document.getElementById('rvtRetake').addEventListener('click', () => {
        ae('tool_stage', { from: 'complete', to: 'retake' });
        renderStart();
    });
    document.getElementById('rvtShare').addEventListener('click', () => share(r, levelName));
    const toPr = document.getElementById('rvtToPractice');
    if (toPr) toPr.addEventListener('click', renderHub);
    panel().querySelectorAll('.rvt-lit-chip-btn').forEach(b => b.addEventListener('click', () => startPractice(b.dataset.topic)));
}

/* ── Разбор по словам ──────────────────────────────────────────────────────
 * Просьба #1512 (Алиса, 28.08.2026): «Не хватает возможности смотреть полные
 * результаты по словам: что верно, что не верно выбрано, какие слова ловушки».
 *
 * ⚠️ Границу «есть правильный ответ / нет правильного ответа» держим ЯВНО.
 * В блоке yes/no человек отвечает О СЕБЕ («знаю это слово»), и сверять там
 * нечего: назвать самоотчёт ошибкой — соврать в лицо тому, кто ответил честно.
 * Настоящая истина есть только у ловушек (выдуманное слово знать нельзя) и у
 * двух блоков с вариантами. Поэтому разделов четыре, а не один список. */
function reviewHtml(r) {
    const R = r.review;
    if (!R) return '';
    const rows = [];

    // 1. Ловушки — ровно то, о чём спрашивали.
    if (R.trapsAll && R.trapsAll.length) {
        const hit = new Set(R.traps || []);
        const chips = R.trapsAll.map(w =>
            `<span class="rvt-rv-chip${hit.has(w) ? ' bad' : ''}">${esc(w)}${hit.has(w) ? ' ✕' : ''}</span>`).join('');
        const desc = hit.size
            ? t('review_traps_desc', 'Этих слов не существует. Отметили как знакомые: :n из :total.')
                .replace(':n', hit.size).replace(':total', R.trapsAll.length)
            : t('review_traps_clean', 'Этих слов не существует — вы не отметили ни одного.');
        rows.push(`<div class="rvt-rv-sec"><div class="rvt-rv-h">${esc(t('review_traps', 'Слова-ловушки'))}</div>
            <div class="rvt-rv-note">${esc(desc)}</div><div class="rvt-rv-chips">${chips}</div></div>`);
    }

    /* Заголовок задания. ⚠️ У половины конструктов грамотности слова НЕТ
     * вовсе: вопрос там — сама формулировка («найдите слово с ошибкой»), а
     * варианты и есть слова. Без этой ветки такие строки выходили с ПУСТОЙ
     * шапкой — «Ваш ответ: подписанные договоры» без вопроса не читается
     * (поймано на снимке экрана, не тестом). */
    const headOf = (x) => {
        if (x.sentence) return x.sentence;
        if (x.word) return x.word;
        return unitView({
            kind: x.construct ? 'lit' : 'mcq',
            construct: x.construct, mtype: x.mtype, word: x.word || '',
            _item: { sentence: x.sentence || '', subtype: x.subtype || '' },
        }).stem;
    };

    // 2 и 3. Блоки с вариантами: задание, ваш выбор, верный ответ.
    const qBlock = (list, titleKey, titleFallback) => {
        if (!list || !list.length) return '';
        const ok = list.filter(x => x.correct).length;
        const items = list.map(x => {
            const head = headOf(x);
            const mine = x.correct
                ? `<span class="rvt-rv-ok">${esc(x.chosen == null ? '—' : x.chosen)}</span>`
                : `<span class="rvt-rv-bad">${esc(x.chosen == null ? '—' : x.chosen)}</span>`;
            const right = x.correct ? '' :
                `<div class="rvt-rv-line"><span>${esc(t('review_right', 'Верно'))}:</span> <b>${esc(x.answer == null ? '—' : x.answer)}</b></div>`;
            return `<div class="rvt-rv-item${x.correct ? '' : ' wrong'}">
                ${head ? `<div class="rvt-rv-q">${esc(head)}</div>` : ''}
                <div class="rvt-rv-line"><span>${esc(t('review_your', 'Ваш ответ'))}:</span> ${mine}</div>
                ${right}</div>`;
        }).join('');
        return `<div class="rvt-rv-sec"><div class="rvt-rv-h">${esc(t(titleKey, titleFallback))}
            <span class="rvt-rv-score">${esc(t('review_score', ':c из :n верно').replace(':c', ok).replace(':n', list.length))}</span></div>
            ${items}</div>`;
    };
    rows.push(qBlock(R.verify, 'review_verify', 'Проверка значения'));
    rows.push(qBlock(R.literacy, 'review_literacy', 'Грамотность'));

    // 4. Самоотчёт — без вердикта, но со значениями: в этом и польза.
    if (R.selfUnknown && R.selfUnknown.length) {
        const items = R.selfUnknown.map(w =>
            `<div class="rvt-rv-word"><b>${esc(w.word)}</b>${w.gloss ? ' — ' + esc(w.gloss) : ''}</div>`).join('');
        rows.push(`<div class="rvt-rv-sec"><div class="rvt-rv-h">${esc(t('review_self', 'Слова, которые вы отметили как незнакомые'))}
            <span class="rvt-rv-score">${R.selfUnknown.length}</span></div>
            <div class="rvt-rv-note">${esc(t('review_self_hint', ''))}</div>${items}</div>`);
    }

    const body = rows.filter(Boolean).join('');
    if (!body) return '';
    return `<details class="rvt-review" id="rvtReview">
        <summary>${esc(t('review_title', 'Разбор по словам'))}<span class="rvt-rv-hint">${esc(t('review_hint', ''))}</span></summary>
        <div class="rvt-rv-body">${body}</div></details>`;
}

function animateNumber(el, target) {
    if (!el) return;
    const dur = 1100, t0 = performance.now();
    const fmt = n => Math.round(n).toLocaleString('ru-RU');
    function tick(now) {
        const k = Math.min(1, (now - t0) / dur);
        const e = 1 - Math.pow(1 - k, 3);
        el.textContent = '≈ ' + fmt(target * e);
        if (k < 1) requestAnimationFrame(tick);
    }
    requestAnimationFrame(tick);
}

// ── Share-card PNG (the viral differentiator: number + level + percentile +
// brand baked into a downloadable 1080² image — nobody in the segment ships one) ──
function roundRect(x, X, Y, w, h, r) {
    x.beginPath();
    x.moveTo(X + r, Y);
    x.arcTo(X + w, Y, X + w, Y + h, r); x.arcTo(X + w, Y + h, X, Y + h, r);
    x.arcTo(X, Y + h, X, Y, r); x.arcTo(X, Y, X + w, Y, r); x.closePath();
}
function drawCard(r, levelName) {
    const brand = cfg.brand || 'Timbrica';
    const domain = brand.toLowerCase() + '.com';
    const S = 1080, c = document.createElement('canvas'); c.width = S; c.height = S;
    const x = c.getContext('2d');
    const F = 'system-ui,-apple-system,"Segoe UI",Roboto,Arial,sans-serif';
    let g = x.createLinearGradient(0, 0, S, S); g.addColorStop(0, '#0c1426'); g.addColorStop(1, '#080d18');
    x.fillStyle = g; x.fillRect(0, 0, S, S);
    const rg = x.createRadialGradient(S / 2, 250, 40, S / 2, 250, 660);
    rg.addColorStop(0, 'rgba(56,189,248,.20)'); rg.addColorStop(1, 'rgba(56,189,248,0)');
    x.fillStyle = rg; x.fillRect(0, 0, S, S);
    // brand row
    x.textAlign = 'left'; x.fillStyle = 'rgba(255,255,255,.94)'; x.font = '800 46px ' + F;
    x.fillText(brand, 90, 132);
    x.textAlign = 'right'; x.fillStyle = 'rgba(148,163,184,.85)'; x.font = '500 31px ' + F;
    x.fillText(t('card_tagline', 'тест на словарный запас'), S - 90, 128);
    // eyebrow
    x.textAlign = 'center';
    try { x.letterSpacing = '4px'; } catch (e) {}
    x.fillStyle = 'rgba(148,163,184,.92)'; x.font = '700 38px ' + F;
    x.fillText((t('card_eyebrow', 'Мой словарный запас')).toUpperCase(), S / 2, 372);
    try { x.letterSpacing = '0px'; } catch (e) {}
    // big number (accent gradient)
    const num = '≈ ' + r.vocabSize.toLocaleString('ru-RU');
    x.font = '900 172px ' + F;
    const ng = x.createLinearGradient(S / 2 - 400, 0, S / 2 + 400, 0);
    ng.addColorStop(0, '#38bdf8'); ng.addColorStop(1, '#818cf8');
    x.fillStyle = ng; x.fillText(num, S / 2, 552);
    x.fillStyle = 'rgba(148,163,184,.92)'; x.font = '500 46px ' + F;
    x.fillText(t('card_words', 'слов'), S / 2, 624);
    // level badge
    x.font = '800 56px ' + F;
    const lw = x.measureText(levelName).width, bw = lw + 96, bh = 108, bx = (S - bw) / 2, by = 700;
    x.fillStyle = 'rgba(56,189,248,.14)'; roundRect(x, bx, by, bw, bh, 54); x.fill();
    x.strokeStyle = 'rgba(56,189,248,.5)'; x.lineWidth = 2; roundRect(x, bx, by, bw, bh, 54); x.stroke();
    x.fillStyle = '#e2e8f0'; x.fillText(levelName, S / 2, by + 73);
    // percentile
    if (r.percentile != null) {
        x.fillStyle = '#7dd3fc'; x.font = '600 40px ' + F;
        x.fillText(t('result_percentile', 'Это больше, чем у :p').replace(':p', r.percentile + '%'), S / 2, 906);
    }
    // cta
    x.fillStyle = 'rgba(148,163,184,.85)'; x.font = '600 34px ' + F;
    x.fillText(t('card_cta', 'проверьте свой словарный запас') + '  ·  ' + domain, S / 2, 1004);
    return c;
}

async function share(r, levelName) {
    ae('tool_stage', { from: 'complete', to: 'share' });
    const url = cfg.shareUrl || location.href.split('#')[0];
    const text = t('share_text', 'Мой словарный запас русского языка: ≈ :n слов (:lvl). Проверь свой:')
        .replace(':n', r.vocabSize.toLocaleString('ru-RU')).replace(':lvl', levelName);
    try {
        const canvas = drawCard(r, levelName);
        const blob = await new Promise(res => canvas.toBlob(res, 'image/png'));
        const file = new File([blob], 'slovarnyy-zapas.png', { type: 'image/png' });
        if (navigator.canShare && navigator.canShare({ files: [file] })) {
            await navigator.share({ files: [file], title: document.title, text, url });
            return;
        }
        if (window.__downloadFile) window.__downloadFile(blob, 'slovarnyy-zapas.png');
        else { const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = 'slovarnyy-zapas.png'; a.click(); }
        window.__toast && window.__toast(t('card_downloaded', 'Картинка сохранена — поделитесь ей в любом чате'), 'success');
    } catch (e) {
        try { await navigator.clipboard.writeText(text + ' ' + url); window.__toast && window.__toast(t('share_copied', 'Скопировано'), 'success'); } catch (_) {}
    }
}

/* ========================================================================
   PRACTICE TRAINER — topic blocks + mastery stars (stage 2).
   Reuses the question banks + unitView/optsHtml; the test stays the snapshot,
   practice is where coverage grows (add a topic = add a card). Progress is
   per-topic mastery stars in localStorage (all tiers; Pro cross-device later). */
const TOPICS = [
    { key: 'def',       kind: 'mcq', name: 'topic_def' },
    { key: 'syn',       kind: 'mcq', name: 'topic_syn' },
    { key: 'stress',    kind: 'lit', name: 'construct_stress' },
    { key: 'paronym',   kind: 'lit', name: 'construct_paronym' },
    { key: 'morphform', kind: 'lit', name: 'construct_morphform' },
    { key: 'pleonasm',  kind: 'lit', name: 'construct_pleonasm' },
    { key: 'punct',     kind: 'lit', name: 'construct_punct' },
    { key: 'nn',        kind: 'lit', name: 'construct_nn' },
    { key: 'ne',        kind: 'lit', name: 'construct_ne' },
    { key: 'prepri',    kind: 'lit', name: 'construct_prepri' },
    { key: 'split',     kind: 'lit', name: 'construct_split' },
    { key: 'root',      kind: 'lit', name: 'construct_root' },
    { key: 'punct2',    kind: 'lit', name: 'construct_punct2' },
    { key: 'orfoep',    kind: 'lit', name: 'construct_orfoep' },
];
// The assessment test samples §B from a fixed core so adding trainer topics never
// lengthens or dilutes it; the trainer covers everything via topicItems().
const TEST_CONSTRUCTS = ['stress', 'paronym', 'morphform', 'pleonasm', 'punct'];
const PRACTICE_N = 10;
const MASTERY_KEY = 'rvt_mastery_v1';
let pr = null;

function loadMastery() { try { return JSON.parse(localStorage.getItem(MASTERY_KEY)) || {}; } catch (e) { return {}; } }
function saveMastery(m) { try { localStorage.setItem(MASTERY_KEY, JSON.stringify(m)); } catch (e) {} }
function starsFor(acc) { return acc == null ? 0 : acc >= 0.95 ? 3 : acc >= 0.8 ? 2 : acc >= 0.6 ? 1 : 0; }
function starsHtml(n) { let s = ''; for (let i = 0; i < 3; i++) s += `<span class="rvt-star${i < n ? ' on' : ''}">★</span>`; return s; }
function topicItems(tp) { return tp.kind === 'mcq' ? mcqItems.filter(m => m.type === tp.key) : litItems.filter(m => m.construct === tp.key); }
function toUnit(item, kind) {
    return kind === 'mcq'
        ? { kind: 'mcq', mtype: item.type, word: item.stem_word, options: item.options, key: item.key, _item: item }
        : { kind: 'lit', construct: item.construct, word: item.word, options: item.options, key: item.key, _item: item };
}

function renderHub() {
    pr = null;
    setPhase(null);
    ae('tool_stage', { from: null, to: 'practice_hub' });
    const m = loadMastery();
    const avail = TOPICS.filter(tp => topicItems(tp).length >= 4);
    const mastered = avail.filter(tp => starsFor((m[tp.key] || {}).best) >= 3).length;
    const cards = avail.map(tp => {
        const rec = m[tp.key] || {};
        const sub = rec.best != null ? t('practice_best', 'Лучший результат: :p%').replace(':p', Math.round(rec.best * 100)) : t('practice_new', 'Ещё не пройдено');
        return `<button class="rvt-topic" data-topic="${esc(tp.key)}">
            <span class="rvt-topic-name">${esc(t(tp.name, tp.key))}</span>
            <span class="rvt-topic-stars">${starsHtml(starsFor(rec.best))}</span>
            <span class="rvt-topic-sub">${esc(sub)}</span></button>`;
    }).join('');
    panel().innerHTML = `
      <div class="rvt-card">
        <button class="rvt-link" id="rvtHubBack">${esc(t('practice_back', '← К тесту'))}</button>
        <h2 class="rvt-h rvt-hub-h">${esc(t('practice_title', 'Тренажёр грамотности'))}</h2>
        <p class="rvt-lead rvt-hub-lead">${esc(t('practice_subtitle', 'Тренируйтесь по темам и отслеживайте прогресс.'))}</p>
        <div class="rvt-hub-prog">${esc(t('practice_progress', 'Освоено :c из :n тем').replace(':c', mastered).replace(':n', avail.length))}</div>
        <div class="rvt-topics">${cards}</div>
      </div>`;
    document.getElementById('rvtHubBack').addEventListener('click', renderStart);
    panel().querySelectorAll('.rvt-topic').forEach(b => b.addEventListener('click', () => startPractice(b.dataset.topic)));
}

function startPractice(topicKey) {
    const topic = TOPICS.find(x => x.key === topicKey);
    if (!topic) return;
    const pool = topicItems(topic).slice();
    for (let i = pool.length - 1; i > 0; i--) { const j = Math.floor(Math.random() * (i + 1)); const tmp = pool[i]; pool[i] = pool[j]; pool[j] = tmp; }
    const items = pool.slice(0, Math.min(PRACTICE_N, pool.length));
    if (!items.length) return renderHub();
    setPhase(null);
    pr = { topic, items, idx: 0, correct: 0, answered: false, current: null };
    ae('tool_stage', { from: 'practice_hub', to: 'practice_run', topic: topicKey });
    renderPracticeQ();
}

function renderPracticeQ() {
    const u = toUnit(pr.items[pr.idx], pr.topic.kind);
    pr.current = u; pr.answered = false;
    const v = unitView(u);
    const prog = Math.round((pr.idx / pr.items.length) * 100);
    const sentHtml = v.sentence ? `<p class="rvt-lit-sentence">${esc(v.sentence)}</p>` : '';
    panel().innerHTML = `
      <div class="rvt-card">
        <div class="rvt-prog"><div class="rvt-prog-f" style="width:${prog}%"></div></div>
        <div class="rvt-pr-count">${esc(t('practice_q_of', 'Вопрос :i из :n').replace(':i', pr.idx + 1).replace(':n', pr.items.length))} · ${esc(t(pr.topic.name, pr.topic.key))}</div>
        <p class="rvt-mcq-stem">${esc(v.stem)}</p>
        ${sentHtml}
        <div class="rvt-opts">${optsHtml(u.options, v.optCls)}</div>
      </div>`;
    panel().querySelectorAll('.rvt-opt').forEach(b => b.addEventListener('click', () => practicePick(parseInt(b.dataset.i, 10), b)));
}

function explainFor(u) {
    const it = u._item || {};
    if (u.kind === 'lit') {
        if (u.construct === 'morphform' && it.fix) return t('expl_morphform', 'Верная форма: :f').replace(':f', it.fix);
        if (['nn', 'split', 'ne', 'prepri', 'root'].indexOf(u.construct) > -1 && it.fix) return t('expl_spelling', 'Верное написание: :f').replace(':f', it.fix);
        if (u.construct === 'pleonasm') return t('expl_pleonasm', '«:w» — лишнее слово: дублирует смысл.').replace(':w', u.options[u.key]);
        if ((u.construct === 'punct' || u.construct === 'punct2') && it.rule) return t('expl_punct', 'Правило: :r').replace(':r', it.rule);
        if (u.construct === 'orfoep') return it.subtype === 'harde'
            ? t('expl_orfoep_harde', 'Здесь согласный перед Е произносится твёрдо.')
            : t('expl_orfoep_chn', 'Здесь сочетание ЧН произносится как [ШН].');
    }
    return '';
}

function practicePick(i, btn) {
    if (!pr || pr.answered) return;
    pr.answered = true;
    const u = pr.current;
    const correct = i === u.key;
    if (correct) pr.correct++;
    panel().querySelectorAll('.rvt-opt').forEach((b, k) => {
        b.disabled = true;
        if (k === u.key) b.classList.add('rvt-opt-correct');
        else if (k === i) b.classList.add('rvt-opt-wrong');
    });
    const expl = explainFor(u);
    const verdict = correct ? t('practice_correct', 'Верно!') : t('practice_wrong', 'Неверно');
    const ansLine = correct ? '' : `<div class="rvt-fb-ans">${esc(t('practice_answer', 'Правильный ответ: :a').replace(':a', u.options[u.key]))}</div>`;
    const last = pr.idx + 1 >= pr.items.length;
    const fb = document.createElement('div');
    fb.className = 'rvt-feedback ' + (correct ? 'ok' : 'bad');
    fb.innerHTML = `<div class="rvt-fb-v">${esc(verdict)}</div>${ansLine}${expl ? `<div class="rvt-fb-expl">${esc(expl)}</div>` : ''}
        <button class="rvt-btn" id="rvtNext">${esc(last ? t('practice_finish', 'Завершить') : t('practice_next', 'Дальше'))}</button>`;
    panel().querySelector('.rvt-card').appendChild(fb);
    const nb = document.getElementById('rvtNext');
    nb.addEventListener('click', practiceNext);
    nb.focus();
}

function practiceNext() {
    if (!pr) return;
    pr.idx++;
    if (pr.idx >= pr.items.length) return practiceFinish();
    renderPracticeQ();
}

function practiceFinish() {
    const acc = pr.items.length ? pr.correct / pr.items.length : 0;
    const m = loadMastery();
    const rec = m[pr.topic.key] || { best: null, attempts: 0 };
    rec.attempts = (rec.attempts || 0) + 1;
    rec.best = rec.best == null ? acc : Math.max(rec.best, acc);
    m[pr.topic.key] = rec; saveMastery(m);
    const stars = starsFor(acc);
    ae('tool_complete', { mode: 'practice', topic: pr.topic.key, score: Math.round(acc * 100), stars });
    const topicKey = pr.topic.key, topicName = t(pr.topic.name, pr.topic.key), correct = pr.correct, total = pr.items.length;
    pr = null;
    panel().innerHTML = `
      <div class="rvt-card rvt-center">
        <div class="rvt-eyebrow">${esc(topicName)}</div>
        <div class="rvt-pr-stars">${starsHtml(stars)}</div>
        <div class="rvt-pr-score">${correct} / ${total}</div>
        <div class="rvt-pr-acc">${Math.round(acc * 100)}%</div>
        <div class="rvt-actions">
          <button class="rvt-btn" id="rvtAgain">${esc(t('practice_again', 'Ещё раз'))}</button>
          <button class="rvt-btn ghost" id="rvtOther">${esc(t('practice_other', 'Другая тема'))}</button>
        </div>
      </div>`;
    document.getElementById('rvtAgain').addEventListener('click', () => startPractice(topicKey));
    document.getElementById('rvtOther').addEventListener('click', renderHub);
}

document.addEventListener('keydown', onKey);
boot();
